import type { Schema } from '@tix/openapi-sdk'

import { type FlowReservation, reservedTicketsByResolver, ticketsValue } from '@/helpers/flowPricing'

type SummaryRow = Schema<'selections_summary_row'>
type FlowInput = Schema<'action_input'>
type FlowPrompt = Schema<'action_prompt'>
type SurveyInput = Extract<FlowInput, { type: 'survey' }>

export type SelectionLine = {
  label: string
  quantity?: number
  value?: string
  price?: number
}

export type SelectionRow = {
  /** Edit target, passed to `goBackTo`. */
  resolverId: string
  /** Narrows the edit to one action. Only the survey needs it, since it shares a resolver with the step that reserved. */
  editType?: string
  heading: string
  /** The inventory the row names, unless the heading already says it. */
  subject?: string
  /** Formatted session datetime, as the API wrote it. */
  detail?: string
  /** Set only when a single unnamed ticket carries the whole amount, as a fixed-price add-on does. */
  price?: number
  lines: SelectionLine[]
  /** False when no prompt was found, so edit would not resolve a step. */
  editable: boolean
  /** True when the user was offered the step but chose nothing (opt-out `pick_n`). */
  skipped: boolean
}

type SelectionsSummarySources = {
  /** `selections_summary._data[0].rows` from the last perform-actions response. */
  rows: SummaryRow[]
  previousInputs: FlowInput[]
  previousPrompts: FlowPrompt[]
  tickets?: Schema<'ticket'>[]
  reservations?: FlowReservation[]
}

const EMPTY_ANSWER = '-'
const SKIPPED = 'Skipped'

const UNNAMED_VARIANT = 'Ticket'

/** Tickets without `flow_resolution_admit_name` collapse under one unnamed key so totals reconcile. */
function ticketsByVariantName(reserved: Schema<'ticket'>[] | undefined): Map<string, Schema<'ticket'>[]> {
  const groups = new Map<string, Schema<'ticket'>[]>()

  for (const ticket of reserved ?? []) {
    const name = ticket.flow_resolution_admit_name || UNNAMED_VARIANT
    const group = groups.get(name)
    if (group) group.push(ticket)
    else groups.set(name, [ticket])
  }

  return groups
}

function surveyLines(answers: Record<string, unknown>, prompt: FlowPrompt | undefined): SelectionLine[] {
  const titles = surveyFieldTitles(prompt)

  return Object.entries(answers).map(([key, answer]) => ({
    label: titles[key] ?? key,
    value: formatAnswer(answer),
  }))
}

/**
 * `additional_info_spec` is typed as an open record, so the shape is narrowed by hand rather than
 * asserted — a malformed spec should degrade to raw field keys, not throw.
 */
function surveyFieldTitles(prompt: FlowPrompt | undefined): Record<string, string> {
  if (prompt?.type !== 'survey') return {}

  const properties = asRecord(asRecord(prompt.options.additional_info_spec)?.properties)
  if (!properties) return {}

  const titles: Record<string, string> = {}
  for (const [key, schema] of Object.entries(properties)) {
    const title = asRecord(schema)?.title
    if (typeof title === 'string' && title.length > 0) titles[key] = title
  }

  return titles
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined
}

function formatAnswer(answer: unknown): string {
  if (answer === null || answer === undefined || answer === '') return EMPTY_ANSWER
  if (Array.isArray(answer)) return answer.length > 0 ? answer.join(', ') : EMPTY_ANSWER
  if (typeof answer === 'boolean') return answer ? 'Yes' : 'No'
  return String(answer)
}

/**
 * Builds the summary the API describes, plus the two things it leaves to the client: prices, from
 * the tickets reserved against each row's resolver, and the survey step, from `previous_inputs`.
 */
export function fromSelectionsSummary({
  rows,
  previousInputs,
  previousPrompts,
  tickets,
  reservations,
}: SelectionsSummarySources): SelectionRow[] {
  const ticketsByResolver = reservedTicketsByResolver(reservations, tickets)
  const pending = previousInputs.filter(isSurveyInput)

  const selectionRows = rows.flatMap((row) => {
    const asked = pending.filter((survey) => survey.resolver_id === row.resolver_id)
    for (const survey of asked) pending.splice(pending.indexOf(survey), 1)

    return [
      selectionRow(row, previousInputs, previousPrompts, ticketsByResolver),
      ...asked.map((survey) => surveyRow(survey, previousPrompts)),
    ]
  })

  return [...selectionRows, ...pending.map((survey) => surveyRow(survey, previousPrompts))]
}

function selectionRow(
  row: SummaryRow,
  previousInputs: FlowInput[],
  previousPrompts: FlowPrompt[],
  ticketsByResolver: Map<string, Schema<'ticket'>[]>,
): SelectionRow {
  // The API leaves the heading empty whenever no `summary_title` is configured.
  const heading = row.heading || row.subject || ''
  const base = { ...editTarget(row, previousInputs, previousPrompts), heading }

  if (isSkipped(row)) return { ...base, lines: [], skipped: true }

  const lines = variantLines(row.variants, ticketsByResolver.get(row.resolver_id))

  if (!row.subject) {
    const prose = (row.values ?? []).map((value) => ({ label: value }))

    return { ...base, lines: lines.length ? lines : prose, skipped: false }
  }

  const values = row.values ?? []
  // A single unnamed ticket says nothing as its own line, so its price moves onto the row itself.
  const [only] = lines
  const collapsed = lines.length === 1 && only?.label === UNNAMED_VARIANT && only.quantity === 1 ? only : undefined

  return {
    ...base,
    // A `pick_time` title is usually the experience name, so the heading can already say the subject.
    subject: row.subject === heading ? undefined : row.subject,
    // Usually one formatted datetime, but joined for the case where the API splits date from time.
    detail: values.length ? values.join(' ') : undefined,
    price: collapsed?.price,
    lines: collapsed ? [] : lines,
    skipped: false,
  }
}

/**
 * Prices join from the tickets reserved against this resolver; an unpriced variant is not held yet.
 * Reserved tickets no variant claims are listed anyway, so the rows reconcile with the subtotal.
 */
function variantLines(variants: SummaryRow['variants'], reserved: Schema<'ticket'>[] | undefined): SelectionLine[] {
  const unclaimed = ticketsByVariantName(reserved)

  const stated = (variants ?? []).map((variant) => {
    const tickets = unclaimed.get(variant.name)
    unclaimed.delete(variant.name)

    return {
      label: variant.name,
      quantity: variant.quantity,
      price: tickets ? ticketsValue(tickets) : undefined,
    }
  })

  const unstated = [...unclaimed].map(([label, tickets]) => ({
    label,
    quantity: tickets.length,
    price: ticketsValue(tickets),
  }))

  return [...stated, ...unstated]
}

/**
 * `goBackTo` rewinds to a resolver's own input where it has one; a leg reached through a `pick_n`
 * that never prompts for itself has none, so the edit returns to the `pick_n` named by `group_id`.
 * Either way it needs both an input to rewind to and the prompt that asked for it.
 *
 * Surveys are discounted: a resolver emits one for the inventory it reserved rather than for the
 * step the user walked, so a bundled leg that reserved a spec would otherwise look prompted and
 * send its edit to the survey `surveyRow` already covers.
 */
function editTarget(
  row: SummaryRow,
  previousInputs: FlowInput[],
  previousPrompts: FlowPrompt[],
): { resolverId: string; editable: boolean } {
  const steps = previousInputs.filter((input) => !isSurveyInput(input))

  const prompted = steps.some((input) => input.resolver_id === row.resolver_id)
  const resolverId = prompted ? row.resolver_id : (row.group_id ?? row.resolver_id)

  const input = steps.find((candidate) => candidate.resolver_id === resolverId)
  const editable =
    input !== undefined &&
    previousPrompts.some((prompt) => prompt.resolver_id === resolverId && prompt.type === input.type)

  return { resolverId, editable }
}

function isSkipped(row: SummaryRow): boolean {
  return row.values?.length === 1 && row.values[0] === SKIPPED
}

function isSurveyInput(input: FlowInput): input is SurveyInput {
  return input.type === 'survey'
}

/**
 * The one step the client builds itself: `survey` is an action rather than a resolver, so the
 * summary has no row for it. The engine emits it from whichever resolver reserved the inventory
 * carrying an `additional_info_spec`.
 */
function surveyRow(survey: SurveyInput, previousPrompts: FlowPrompt[]): SelectionRow {
  const prompt = findSurveyPrompt(survey, previousPrompts)

  return {
    resolverId: survey.resolver_id,
    editType: 'survey',
    heading: prompt?.meta?.summary_title || prompt?.meta?.prompt || 'Additional information',
    lines: surveyLines(survey.input.additional_info, prompt),
    editable: prompt !== undefined,
    skipped: false,
  }
}

/** Matched on type as well as resolver, since the resolver prompted for the inventory too. */
function findSurveyPrompt(survey: SurveyInput, previousPrompts: FlowPrompt[]): FlowPrompt | undefined {
  return previousPrompts.find((prompt) => prompt.resolver_id === survey.resolver_id && prompt.type === 'survey')
}
